/**
 * Runtime evaluator dla formatowania warunkowego — Krok 7.1 kreatora widoków
 * list. Pure-TS funkcje (bez React, bez DOM), przyjmuje wiersze + reguły,
 * zwraca finalne style komórek/wierszy + dekoracje (data_bar, icon_set).
 *
 * Wzorzec użycia w ConfigurableTable (Krok 7.3):
 *
 *   const context = useMemo(
 *     () => precomputeRuleContext(rows, formattingRules, getValue),
 *     [rows, formattingRules]
 *   );
 *
 *   // per komórka:
 *   const result = evaluateFormattingRules({
 *     row, rowIndex, columnId, rules: formattingRules, context, getValue
 *   });
 *   // <td style={result.cellStyle}> ...
 *   // <tr style={result.rowStyle}> ...
 *
 * Wszystkie 7 typów reguł obsłużone:
 *  - highlight_cells — operator + value (gt/lt/eq/neq/contains/.../between)
 *  - top_bottom — top|bottom × count|percent × n
 *  - above_below_avg — above/below + or_equal warianty (avg liczone dyn.)
 *  - unique_duplicate — Set wartości występujących >1 raz
 *  - changing_values — wartość różna od poprzedniej po sortowaniu
 *  - all_cells — gradient 2/3-color, data bar, icon set
 *  - formula — własny mini-parser AST (recursive descent, white-list ops)
 *
 * Brak zewnętrznych zależności (zero-deps), brak `eval`.
 */

import type { CSSProperties } from 'react';
import type {
  AboveBelowAvgCondition,
  AllCellsCondition,
  CellStyle,
  ChangingValuesCondition,
  FormattingRule,
  FormulaCondition,
  HighlightCellsCondition,
  TopBottomCondition,
  UniqueDuplicateCondition,
} from '@saas/shared';

// ── Typy publiczne ───────────────────────────────────────────────────────────

/**
 * Wynik ewaluacji reguł dla jednej komórki. `cellStyle` aplikowane do
 * `<td>`, `rowStyle` do `<tr>`, `decoration` do specjalnych renderów
 * (pasek pod tłem / ikona obok wartości).
 */
export interface EvaluateResult {
  cellStyle: CSSProperties;
  rowStyle: CSSProperties;
  decoration?: CellDecoration;
}

export interface CellDecoration {
  dataBar?: {
    color: string;
    /** Procent szerokości paska 0–100. */
    percent: number;
    showValue: boolean;
  };
  iconSet?: {
    setName: 'arrows_3' | 'arrows_5' | 'traffic_3' | 'stars_5';
    /** Index ikony 0..(set size - 1). */
    iconIndex: number;
    reverse: boolean;
  };
}

/**
 * Pre-computed kontekst dla reguł — liczony raz per (rows, rules) i
 * współdzielony przez wszystkie wywołania evaluate dla danego renderu.
 *
 * Klucz: `columnId`. Wartość: agregaty dla tej kolumny.
 */
export interface PrecomputedContext {
  /** Min/max/avg per kolumna (tylko dla wartości numerycznych). */
  numericStats: Map<string, { min: number; max: number; avg: number; count: number }>;
  /** Zbiór wartości występujących >1 raz per kolumna (dla unique_duplicate). */
  duplicates: Map<string, Set<string>>;
  /** Posortowane row indexes ASC po wartości danej kolumny (dla top_bottom + changing). */
  sortedAsc: Map<string, number[]>;
  /** Posortowane row indexes DESC po wartości danej kolumny (dla top_bottom). */
  sortedDesc: Map<string, number[]>;
}

export type GetValueFn<T> = (row: T, columnId: string) => unknown;

export interface EvaluateArgs<T> {
  row: T;
  rowIndex: number;
  columnId: string;
  rules: FormattingRule[];
  context: PrecomputedContext;
  getValue: GetValueFn<T>;
  /** Wszystkie wiersze — wymagane dla changing_values (porównanie z poprzednim). */
  allRows: T[];
}

// ── Pre-compute kontekstu ────────────────────────────────────────────────────

/**
 * Buduje agregaty per kolumna na podstawie aktualnego zbioru wierszy.
 * Wywoływane raz przy mount/refresh listy (memoizowane przez useMemo
 * w komponencie ConfigurableTable po stronie woła­jącego).
 *
 * Operuje tylko na kolumnach które są wymienione w jakiejkolwiek regule —
 * unika niepotrzebnego przeliczania.
 */
export function precomputeRuleContext<T>(
  rows: T[],
  rules: FormattingRule[],
  getValue: GetValueFn<T>,
): PrecomputedContext {
  const columnsInRules = new Set<string>();
  for (const rule of rules) {
    if (!rule.enabled) continue;
    columnsInRules.add(rule.columnId);
    if (rule.type === 'changing_values') {
      columnsInRules.add(rule.condition.sortColumn);
    }
  }

  const numericStats = new Map<string, { min: number; max: number; avg: number; count: number }>();
  const duplicates = new Map<string, Set<string>>();
  const sortedAsc = new Map<string, number[]>();
  const sortedDesc = new Map<string, number[]>();

  for (const columnId of columnsInRules) {
    // Numeric stats — number lub string w pełni numeryczny (pieniądze z Postgresa
    // wracają jako string „1234.56", NIP jako „9623572368"). NaN/null/puste pomijane.
    let min = Number.POSITIVE_INFINITY;
    let max = Number.NEGATIVE_INFINITY;
    let sum = 0;
    let count = 0;
    for (const row of rows) {
      const v = toNumber(getValue(row, columnId));
      if (v !== null) {
        if (v < min) min = v;
        if (v > max) max = v;
        sum += v;
        count++;
      }
    }
    if (count > 0) {
      numericStats.set(columnId, { min, max, avg: sum / count, count });
    }

    // Duplicates — wartości występujące >1 raz (Map<value, count> → Set duplikatów).
    const valueCounts = new Map<string, number>();
    for (const row of rows) {
      const v = getValue(row, columnId);
      const key = stringifyForDedupe(v);
      valueCounts.set(key, (valueCounts.get(key) ?? 0) + 1);
    }
    const dupes = new Set<string>();
    for (const [key, c] of valueCounts) {
      if (c > 1) dupes.add(key);
    }
    duplicates.set(columnId, dupes);

    // Sorted indexes — ASC + DESC po wartości, dla top_bottom + changing.
    // `a`/`b` pochodzą z indexes wygenerowanych z `rows.map(...)`, więc rows[a]/[b]
    // są zawsze w zakresie — wymuszamy nie-undefined assertion.
    const indexes = rows.map((_, i) => i);
    const asc = [...indexes].sort((a, b) =>
      compareForSort(getValue(rows[a]!, columnId), getValue(rows[b]!, columnId)),
    );
    const desc = [...indexes].sort((a, b) =>
      compareForSort(getValue(rows[b]!, columnId), getValue(rows[a]!, columnId)),
    );
    sortedAsc.set(columnId, asc);
    sortedDesc.set(columnId, desc);
  }

  return { numericStats, duplicates, sortedAsc, sortedDesc };
}

function stringifyForDedupe(v: unknown): string {
  if (v === null || v === undefined) return ' NULL ';
  if (typeof v === 'string') return `s:${v}`;
  if (typeof v === 'number') return `n:${v}`;
  if (typeof v === 'boolean') return `b:${v}`;
  return `j:${JSON.stringify(v)}`;
}

function compareForSort(a: unknown, b: unknown): number {
  if (a === null || a === undefined) return b === null || b === undefined ? 0 : 1;
  if (b === null || b === undefined) return -1;
  // Numerycznie gdy oba dają się skoercować (np. kwoty/NIP jako string) —
  // inaczej top-N sortowałby „100" < „90" leksykalnie.
  const na = toNumber(a);
  const nb = toNumber(b);
  if (na !== null && nb !== null) return na - nb;
  return String(a).localeCompare(String(b), 'pl');
}

/**
 * Koercja wartości do liczby dla reguł liczbowych (gradient / top-N / średnia /
 * operatory >,<,między w highlight_cells). `number` → number; string w PEŁNI
 * numeryczny („1234.56", „9623572368") → number; wszystko inne (puste, tekst,
 * bool, NaN) → null. Postgres `numeric` wraca jako string, stąd ta koercja.
 */
function toNumber(v: unknown): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'string') {
    const trimmed = v.trim();
    if (trimmed === '') return null;
    const n = Number(trimmed);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

// ── Główna funkcja evaluate ──────────────────────────────────────────────────

/**
 * Ewaluuje wszystkie reguły dotyczące danej komórki i zwraca finalny styl.
 *
 * Algorytm:
 *  1. Filter: tylko reguły z `enabled=true` i `columnId === args.columnId`.
 *  2. Sort po `priority` ASC (niższa = wyższy priorytet, last-wins per
 *     pojedyncze pole stylu).
 *  3. Per reguła: ewaluuj warunek; jeśli match → merge style do
 *     cellStyle (applyTo='cell') albo rowStyle (applyTo='row');
 *     dla all_cells generuj dekorację albo style gradient.
 *
 * Brak match = brak zmiany stylu (komórka renderuje się z defaultami).
 */
export function evaluateFormattingRules<T>(args: EvaluateArgs<T>): EvaluateResult {
  const applicable = args.rules
    .filter((r) => r.enabled && r.columnId === args.columnId)
    .sort((a, b) => a.priority - b.priority);

  const cellStyle: CSSProperties = {};
  const rowStyle: CSSProperties = {};
  let decoration: CellDecoration | undefined;
  const value = args.getValue(args.row, args.columnId);

  for (const rule of applicable) {
    const matched = matchesRule(rule, value, args);
    if (!matched) continue;

    // all_cells generuje styl gradient + ewentualne dekoracje.
    if (rule.type === 'all_cells') {
      const out = applyAllCellsRule(rule.condition, value, args.context, args.columnId);
      if (rule.applyTo === 'cell') {
        Object.assign(cellStyle, out.style);
        if (out.decoration) decoration = { ...decoration, ...out.decoration };
      } else {
        Object.assign(rowStyle, out.style);
      }
      continue;
    }

    // Pozostałe typy: style z reguły (CellStyle → CSSProperties).
    const cssFromStyle = cellStyleToCss(rule.style);
    if (rule.applyTo === 'cell') {
      Object.assign(cellStyle, cssFromStyle);
    } else {
      Object.assign(rowStyle, cssFromStyle);
    }
  }

  return decoration ? { cellStyle, rowStyle, decoration } : { cellStyle, rowStyle };
}

/**
 * Konwertuje CellStyle (kontrakt @saas/shared) na CSSProperties Reacta.
 */
function cellStyleToCss(style: CellStyle): CSSProperties {
  const out: CSSProperties = {};
  if (style.backgroundColor) out.backgroundColor = style.backgroundColor;
  if (style.color) out.color = style.color;
  if (style.borderColor) {
    out.borderColor = style.borderColor;
    out.borderStyle = 'solid';
  }
  if (style.borderWidth) out.borderWidth = `${style.borderWidth}px`;
  if (style.fontWeight) out.fontWeight = style.fontWeight;
  if (style.fontStyle) out.fontStyle = style.fontStyle;
  return out;
}

// ── Match dispatch per typ reguły ────────────────────────────────────────────

function matchesRule<T>(rule: FormattingRule, value: unknown, args: EvaluateArgs<T>): boolean {
  switch (rule.type) {
    case 'highlight_cells':
      return matchesHighlightCells(value, rule.condition);
    case 'top_bottom':
      return matchesTopBottom(args.rowIndex, args.context, rule.columnId, rule.condition);
    case 'above_below_avg':
      return matchesAboveBelowAvg(value, args.context, rule.columnId, rule.condition);
    case 'unique_duplicate':
      return matchesUniqueDuplicate(value, args.context, rule.columnId, rule.condition);
    case 'changing_values':
      return matchesChangingValues(args.rowIndex, args.allRows, args.getValue, rule.condition);
    case 'all_cells':
      // Match gdy wartość jest numeryczna (number lub string numeryczny —
      // gradient pokrywa cały zakres wartości kolumny).
      return toNumber(value) !== null;
    case 'formula':
      return matchesFormula(args.row, args.getValue, rule.condition);
    default:
      return false;
  }
}

// ── Typ 1 — highlight_cells ──────────────────────────────────────────────────

function matchesHighlightCells(value: unknown, condition: HighlightCellsCondition): boolean {
  const op = condition.operator;
  if (op === 'is_null') return value === null || value === undefined || value === '';
  if (op === 'is_not_null') return value !== null && value !== undefined && value !== '';

  const target = condition.value;
  const target2 = condition.value2;

  if (op === 'between' || op === 'not_between') {
    const v = toNumber(value);
    const t1 = toNumber(target);
    const t2 = toNumber(target2);
    if (v === null || t1 === null || t2 === null) return false;
    const inRange = v >= t1 && v <= t2;
    return op === 'between' ? inRange : !inRange;
  }

  if (target === null || target === undefined) return false;

  switch (op) {
    case 'eq':
      return value === target;
    case 'neq':
      return value !== target;
    case 'gt': {
      const v = toNumber(value);
      const tt = toNumber(target);
      return v !== null && tt !== null && v > tt;
    }
    case 'gte': {
      const v = toNumber(value);
      const tt = toNumber(target);
      return v !== null && tt !== null && v >= tt;
    }
    case 'lt': {
      const v = toNumber(value);
      const tt = toNumber(target);
      return v !== null && tt !== null && v < tt;
    }
    case 'lte': {
      const v = toNumber(value);
      const tt = toNumber(target);
      return v !== null && tt !== null && v <= tt;
    }
    case 'contains':
      return String(value ?? '')
        .toLowerCase()
        .includes(String(target).toLowerCase());
    case 'not_contains':
      return !String(value ?? '')
        .toLowerCase()
        .includes(String(target).toLowerCase());
    case 'starts_with':
      return String(value ?? '')
        .toLowerCase()
        .startsWith(String(target).toLowerCase());
    case 'ends_with':
      return String(value ?? '')
        .toLowerCase()
        .endsWith(String(target).toLowerCase());
    default:
      return false;
  }
}

// ── Typ 2 — top_bottom ───────────────────────────────────────────────────────

function matchesTopBottom(
  rowIndex: number,
  context: PrecomputedContext,
  columnId: string,
  condition: TopBottomCondition,
): boolean {
  const sorted = condition.mode === 'top' ? context.sortedDesc : context.sortedAsc;
  const indexes = sorted.get(columnId);
  if (!indexes || indexes.length === 0) return false;

  const cutoff =
    condition.unit === 'count'
      ? Math.min(condition.n, indexes.length)
      : Math.max(1, Math.floor((condition.n / 100) * indexes.length));

  return indexes.slice(0, cutoff).includes(rowIndex);
}

// ── Typ 3 — above_below_avg ──────────────────────────────────────────────────

function matchesAboveBelowAvg(
  value: unknown,
  context: PrecomputedContext,
  columnId: string,
  condition: AboveBelowAvgCondition,
): boolean {
  const num = toNumber(value);
  if (num === null) return false;
  const stats = context.numericStats.get(columnId);
  if (!stats) return false;
  switch (condition.mode) {
    case 'above':
      return num > stats.avg;
    case 'below':
      return num < stats.avg;
    case 'above_or_equal':
      return num >= stats.avg;
    case 'below_or_equal':
      return num <= stats.avg;
    default:
      return false;
  }
}

// ── Typ 4 — unique_duplicate ─────────────────────────────────────────────────

function matchesUniqueDuplicate(
  value: unknown,
  context: PrecomputedContext,
  columnId: string,
  condition: UniqueDuplicateCondition,
): boolean {
  const dupes = context.duplicates.get(columnId);
  if (!dupes) return false;
  const key = stringifyForDedupe(value);
  const isDuplicate = dupes.has(key);
  return condition.mode === 'duplicate' ? isDuplicate : !isDuplicate;
}

// ── Typ 5 — changing_values ──────────────────────────────────────────────────

function matchesChangingValues<T>(
  rowIndex: number,
  allRows: T[],
  getValue: GetValueFn<T>,
  condition: ChangingValuesCondition,
): boolean {
  if (rowIndex === 0) return true; // pierwszy wiersz zawsze „zmieniający się"
  const currentRow = allRows[rowIndex];
  const previousRow = allRows[rowIndex - 1];
  if (!currentRow || !previousRow) return false;
  const current = getValue(currentRow, condition.sortColumn);
  const previous = getValue(previousRow, condition.sortColumn);
  return stringifyForDedupe(current) !== stringifyForDedupe(previous);
}

// ── Typ 6 — all_cells (gradient / data_bar / icon_set) ───────────────────────

interface AllCellsOutput {
  style: CSSProperties;
  decoration?: CellDecoration;
}

function applyAllCellsRule(
  condition: AllCellsCondition,
  value: unknown,
  context: PrecomputedContext,
  columnId: string,
): AllCellsOutput {
  const stats = context.numericStats.get(columnId);
  const num = toNumber(value);
  if (!stats || num === null) return { style: {} };

  const range = stats.max - stats.min;
  const t = range === 0 ? 0.5 : (num - stats.min) / range;

  switch (condition.subType) {
    case '2color':
      return {
        style: { backgroundColor: interpolateHex(condition.colorMin, condition.colorMax, t) },
      };
    case '3color': {
      const mid = (condition.midpoint ?? 50) / 100;
      const color =
        t <= mid
          ? interpolateHex(condition.colorMin, condition.colorMid, t / mid)
          : interpolateHex(condition.colorMid, condition.colorMax, (t - mid) / (1 - mid));
      return { style: { backgroundColor: color } };
    }
    case 'data_bar':
      return {
        style: {},
        decoration: {
          dataBar: {
            color: condition.color,
            percent: Math.max(0, Math.min(100, t * 100)),
            showValue: condition.showValue ?? true,
          },
        },
      };
    case 'icon_set': {
      const sizes = { arrows_3: 3, arrows_5: 5, traffic_3: 3, stars_5: 5 } as const;
      const size = sizes[condition.setName];
      const iconIndex = Math.min(size - 1, Math.floor(t * size));
      return {
        style: {},
        decoration: {
          iconSet: {
            setName: condition.setName,
            iconIndex,
            reverse: condition.reverse ?? false,
          },
        },
      };
    }
  }
}

/**
 * Linear interpolation między dwoma kolorami hex `#RRGGBB`.
 * `t` ∈ [0, 1]. Zwraca hex `#RRGGBB`.
 */
export function interpolateHex(c1: string, c2: string, t: number): string {
  const clamped = Math.max(0, Math.min(1, t));
  const r1 = parseInt(c1.slice(1, 3), 16);
  const g1 = parseInt(c1.slice(3, 5), 16);
  const b1 = parseInt(c1.slice(5, 7), 16);
  const r2 = parseInt(c2.slice(1, 3), 16);
  const g2 = parseInt(c2.slice(3, 5), 16);
  const b2 = parseInt(c2.slice(5, 7), 16);
  const r = Math.round(r1 + (r2 - r1) * clamped);
  const g = Math.round(g1 + (g2 - g1) * clamped);
  const b = Math.round(b1 + (b2 - b1) * clamped);
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b
    .toString(16)
    .padStart(2, '0')}`;
}

// ── Typ 7 — formula (mini-parser AST) ────────────────────────────────────────
//
// Składnia (BNF):
//   expression = orExpr
//   orExpr     = andExpr ( "OR" andExpr )*
//   andExpr    = notExpr ( "AND" notExpr )*
//   notExpr    = "NOT" comparison | comparison
//   comparison = primary ( cmpOp primary )?
//   primary    = literal | reference | "(" expression ")"
//   literal    = number | string | "true" | "false" | "null"
//   reference  = "@" identifier
//   cmpOp      = ">" | "<" | ">=" | "<=" | "=" | "!="
//
// Brak `eval`, brak `new Function`. Parser pure-TS.

interface Token {
  type:
    | 'number'
    | 'string'
    | 'identifier'
    | 'ref'
    | 'op'
    | 'paren_open'
    | 'paren_close'
    | 'keyword';
  value: string;
}

function tokenizeFormula(expr: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;
  while (i < expr.length) {
    const c = expr.charAt(i);
    if (c === ' ' || c === '\t' || c === '\n') {
      i++;
      continue;
    }
    if (c === '(') {
      tokens.push({ type: 'paren_open', value: '(' });
      i++;
      continue;
    }
    if (c === ')') {
      tokens.push({ type: 'paren_close', value: ')' });
      i++;
      continue;
    }
    if (c === '@') {
      let j = i + 1;
      while (j < expr.length && /[a-zA-Z0-9_]/.test(expr.charAt(j))) j++;
      tokens.push({ type: 'ref', value: expr.slice(i + 1, j) });
      i = j;
      continue;
    }
    if (c === "'") {
      let j = i + 1;
      while (j < expr.length && expr.charAt(j) !== "'") j++;
      if (j >= expr.length) throw new Error(`Unterminated string starting at ${i}`);
      tokens.push({ type: 'string', value: expr.slice(i + 1, j) });
      i = j + 1;
      continue;
    }
    if (/[0-9]/.test(c) || (c === '-' && /[0-9]/.test(expr.charAt(i + 1)))) {
      let j = i + 1;
      while (j < expr.length && /[0-9.]/.test(expr.charAt(j))) j++;
      tokens.push({ type: 'number', value: expr.slice(i, j) });
      i = j;
      continue;
    }
    if (c === '>' || c === '<' || c === '=' || c === '!') {
      // 2-char operators: >=, <=, !=, ==
      const next = expr.charAt(i + 1);
      if ((c === '>' || c === '<' || c === '!') && next === '=') {
        tokens.push({ type: 'op', value: c + next });
        i += 2;
        continue;
      }
      tokens.push({ type: 'op', value: c });
      i++;
      continue;
    }
    if (/[a-zA-Z_]/.test(c)) {
      let j = i + 1;
      while (j < expr.length && /[a-zA-Z0-9_]/.test(expr.charAt(j))) j++;
      const word = expr.slice(i, j);
      const upper = word.toUpperCase();
      if (upper === 'AND' || upper === 'OR' || upper === 'NOT') {
        tokens.push({ type: 'keyword', value: upper });
      } else if (word === 'true' || word === 'false' || word === 'null') {
        tokens.push({ type: 'keyword', value: word });
      } else {
        tokens.push({ type: 'identifier', value: word });
      }
      i = j;
      continue;
    }
    throw new Error(`Unexpected character '${c}' at position ${i}`);
  }
  return tokens;
}

interface ParseState {
  tokens: Token[];
  pos: number;
}

function peek(state: ParseState): Token | undefined {
  return state.tokens[state.pos];
}

function consume(state: ParseState): Token {
  const t = state.tokens[state.pos];
  if (!t) throw new Error('Unexpected end of expression');
  state.pos++;
  return t;
}

type FormulaValue = string | number | boolean | null;

function parseExpression<T>(state: ParseState, row: T, getValue: GetValueFn<T>): FormulaValue {
  return parseOr(state, row, getValue);
}

function parseOr<T>(state: ParseState, row: T, getValue: GetValueFn<T>): FormulaValue {
  let left = parseAnd(state, row, getValue);
  while (peek(state)?.type === 'keyword' && peek(state)?.value === 'OR') {
    consume(state);
    const right = parseAnd(state, row, getValue);
    left = Boolean(left) || Boolean(right);
  }
  return left;
}

function parseAnd<T>(state: ParseState, row: T, getValue: GetValueFn<T>): FormulaValue {
  let left = parseNot(state, row, getValue);
  while (peek(state)?.type === 'keyword' && peek(state)?.value === 'AND') {
    consume(state);
    const right = parseNot(state, row, getValue);
    left = Boolean(left) && Boolean(right);
  }
  return left;
}

function parseNot<T>(state: ParseState, row: T, getValue: GetValueFn<T>): FormulaValue {
  if (peek(state)?.type === 'keyword' && peek(state)?.value === 'NOT') {
    consume(state);
    const inner = parseComparison(state, row, getValue);
    return !inner;
  }
  return parseComparison(state, row, getValue);
}

function parseComparison<T>(state: ParseState, row: T, getValue: GetValueFn<T>): FormulaValue {
  const left = parsePrimary(state, row, getValue);
  const op = peek(state);
  if (op?.type === 'op') {
    consume(state);
    const right = parsePrimary(state, row, getValue);
    return compareFormulaValues(left, op.value, right);
  }
  return left;
}

function parsePrimary<T>(state: ParseState, row: T, getValue: GetValueFn<T>): FormulaValue {
  const t = consume(state);
  if (t.type === 'paren_open') {
    const inner = parseExpression(state, row, getValue);
    const close = consume(state);
    if (close.type !== 'paren_close') throw new Error(`Expected ')', got '${close.value}'`);
    return inner;
  }
  if (t.type === 'number') return parseFloat(t.value);
  if (t.type === 'string') return t.value;
  if (t.type === 'keyword') {
    if (t.value === 'true') return true;
    if (t.value === 'false') return false;
    if (t.value === 'null') return null;
    throw new Error(`Unexpected keyword '${t.value}'`);
  }
  if (t.type === 'ref') {
    const v = getValue(row, t.value);
    if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean' || v === null) {
      return v;
    }
    return v === undefined ? null : String(v);
  }
  throw new Error(`Unexpected token '${t.value}'`);
}

function compareFormulaValues(left: FormulaValue, op: string, right: FormulaValue): boolean {
  switch (op) {
    case '=':
    case '==':
      return left === right;
    case '!=':
      return left !== right;
    case '>':
      return typeof left === 'number' && typeof right === 'number' && left > right;
    case '>=':
      return typeof left === 'number' && typeof right === 'number' && left >= right;
    case '<':
      return typeof left === 'number' && typeof right === 'number' && left < right;
    case '<=':
      return typeof left === 'number' && typeof right === 'number' && left <= right;
    default:
      throw new Error(`Unknown operator '${op}'`);
  }
}

function matchesFormula<T>(row: T, getValue: GetValueFn<T>, condition: FormulaCondition): boolean {
  try {
    const tokens = tokenizeFormula(condition.expression);
    const state: ParseState = { tokens, pos: 0 };
    const result = parseExpression(state, row, getValue);
    if (state.pos < tokens.length) {
      // Pozostały niewykorzystane tokeny — błąd składni.
      return false;
    }
    return Boolean(result);
  } catch {
    // Błąd parsera / ewaluatora → reguła nie matchuje (silent fail dla
    // robustness — UI walidacji powinien wyłapać przy zapisie reguły).
    return false;
  }
}
